import "./NewTemplate.css";
import { useState, useEffect, useRef, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import useVisible from "../../hooks/useVisible.jsx";
import Modal from "../Modal/Modal";
import { getCachedScript } from "../../utils/scriptCache.js";
import {
  textTemplate,
  transitionTemplate,
  slideshowTemplate,
} from "../../utils/templatePreset.js";
import { setItem } from "../../utils/cepStorage";

const CUSTOM_TEMPLATE_PREFIX = "custom:";
const isCustomTemplateType = (value) =>
  typeof value === "string" && value.startsWith(CUSTOM_TEMPLATE_PREFIX);
const getCustomTemplateId = (value) =>
  isCustomTemplateType(value) ? value.slice(CUSTOM_TEMPLATE_PREFIX.length) : null;
const buildCustomTemplateValue = (id) => `${CUSTOM_TEMPLATE_PREFIX}${id}`;
const cloneTemplate = (template) =>
  template ? JSON.parse(JSON.stringify(template)) : null;

const createTemplateItem = (type) => ({
  id: uuidv4(),
  type,
  name: type === "folder" ? "New Folder" : "New Composition",
  include: type === "folder" ? [] : false,
});

const RESOLUTION_PRESETS = [
  { value: "full_hd", label: "Full HD (1920x1080)", dimensions: [1920, 1080] },
  { value: "2k", label: "2K (2560x1440)", dimensions: [2560, 1440] },
  { value: "4k", label: "4K (3840x2160)", dimensions: [3840, 2160] },
  { value: "square", label: "Square (1080x1080)", dimensions: [1080, 1080] },
  { value: "post", label: "Post (1080x1350)", dimensions: [1080, 1350] },
  { value: "vertical", label: "Vertical (1080x1920)", dimensions: [1080, 1920] },
];

const updateItemName = (items, itemId, newName) =>
  items.map((item) => {
    if (item.id === itemId) {
      return { ...item, name: newName };
    }

    if (Array.isArray(item.include)) {
      return { ...item, include: updateItemName(item.include, itemId, newName) };
    }

    return item;
  });

const addChildToItems = (items, parentId, newItem) =>
  items.map((item) => {
    if (item.id === parentId && item.type === "folder") {
      const include = Array.isArray(item.include) ? item.include : [];
      return { ...item, include: [...include, newItem] };
    }

    if (Array.isArray(item.include)) {
      return { ...item, include: addChildToItems(item.include, parentId, newItem) };
    }

    return item;
  });

const removeItemById = (items, itemId) => {
  let removed = null;
  const updatedItems = items
    .map((item) => {
      if (item.id === itemId) {
        removed = item;
        return null;
      }
      if (Array.isArray(item.include)) {
        const result = removeItemById(item.include, itemId);
        if (result.removed) {
          removed = result.removed;
          return { ...item, include: result.items };
        }
      }
      return item;
    })
    .filter(Boolean);

  return { items: updatedItems, removed };
};

const findItemById = (items, itemId) => {
  for (const item of items || []) {
    if (item.id === itemId) {
      return item;
    }
    if (Array.isArray(item.include)) {
      const found = findItemById(item.include, itemId);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const treeContainsId = (items, itemId) => {
  for (const item of items || []) {
    if (item.id === itemId) {
      return true;
    }
    if (Array.isArray(item.include) && treeContainsId(item.include, itemId)) {
      return true;
    }
  }
  return false;
};

const isDescendant = (items, ancestorId, targetId) => {
  const ancestor = findItemById(items, ancestorId);
  if (!ancestor || !Array.isArray(ancestor.include)) {
    return false;
  }
  return treeContainsId(ancestor.include, targetId);
};

const insertItemRelative = (items, targetId, position, newItem) => {
  const updated = [];
  let inserted = false;

  for (const item of items) {
    if (item.id === targetId) {
      if (position === "before") {
        updated.push(newItem, item);
        inserted = true;
      } else if (position === "after") {
        updated.push(item, newItem);
        inserted = true;
      } else if (position === "inside" && item.type === "folder") {
        const include = Array.isArray(item.include) ? item.include : [];
        updated.push({ ...item, include: [...include, newItem] });
        inserted = true;
      } else {
        updated.push(item);
      }
    } else if (Array.isArray(item.include)) {
      const childResult = insertItemRelative(item.include, targetId, position, newItem);
      if (childResult.inserted) {
        updated.push({ ...item, include: childResult.items });
        inserted = true;
      } else {
        updated.push(item);
      }
    } else {
      updated.push(item);
    }
  }

  return { items: updated, inserted };
};

const moveItemInTree = (items, dragId, targetId, position) => {
  const removal = removeItemById(items, dragId);
  if (!removal.removed) {
    return { items, moved: false };
  }

  const insertion = insertItemRelative(removal.items, targetId, position, removal.removed);
  if (!insertion.inserted) {
    return { items, moved: false };
  }

  return { items: insertion.items, moved: true };
};

function TemplateItem({
  item,
  depth,
  focusedId,
  onNameChange,
  onAddChild,
  onDelete,
  collapsedItems,
  onToggleCollapse,
  draggingItemId,
  dropTarget,
  onDragStart,
  onDragEnd,
  onDragLeave,
  onItemDrop,
  setDropTarget,
  canDrop,
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (focusedId === item.id && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [focusedId, item.id]);

  const isFolder = item.type === "folder";
  const hasChildren = isFolder && Array.isArray(item.include) && item.include.length > 0;
  const isCollapsed = Boolean(collapsedItems[item.id]);
  const isCurrentDropTarget = dropTarget?.id === item.id;
  const currentDropPosition = isCurrentDropTarget ? dropTarget.position : null;
  const itemClasses = [
    "template-editor__item",
    hasChildren ? "template-editor__item--folder" : "template-editor__item--leaf",
    depth > 0 ? "template-editor__item--nested" : "",
    isCollapsed ? "template-editor__item--collapsed" : "",
    isCurrentDropTarget && currentDropPosition
      ? `template-editor__item--drop-${currentDropPosition}`
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  const mainClasses = [
    "template-editor__item-main",
    !isFolder ? "template-editor__item-main--leaf" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const calculateDropPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetY = event.clientY - rect.top;
    if (offsetY < rect.height / 3) {
      return "before";
    }
    if (offsetY > (rect.height * 2) / 3) {
      return "after";
    }
    if (isFolder) {
      return "inside";
    }
    return offsetY < rect.height / 2 ? "before" : "after";
  };

  const handleDragOver = (event) => {
    if (!draggingItemId || draggingItemId === item.id) {
      return;
    }
    const position = calculateDropPosition(event);
    if (!canDrop(item.id, position)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    setDropTarget((prev) => {
      if (prev?.id === item.id && prev?.position === position) {
        return prev;
      }
      return { id: item.id, position };
    });
  };

  const handleDrop = (event) => {
    if (!draggingItemId || draggingItemId === item.id) {
      return;
    }
    const position = calculateDropPosition(event);
    if (!canDrop(item.id, position)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    onItemDrop(item.id, position);
  };

  const handleDragLeave = (event) => {
    const nextTarget = event.relatedTarget;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      onDragLeave(item.id);
    }
  };

  const handleDragStart = (event) => {
    event.stopPropagation();
    onDragStart(item.id);
    event.dataTransfer.effectAllowed = "move";
    try {
      event.dataTransfer.setData("text/plain", item.id);
    } catch (error) {
      // ignored for unsupported browsers
    }
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  return (
    <div
      className="template-editor__item-wrapper"
      style={{ marginLeft: depth * 16 }}
    >
      <div
        className={itemClasses}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      >
        <div className={mainClasses}>
          <span
            className="template-editor__drag-handle"
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            title="Drag to reorder"
          >
            <i className="fa-solid fa-grip-vertical"></i>
          </span>
          {isFolder ? (
            <button
              type="button"
              className={`template-editor__toggle ${
                !hasChildren ? "template-editor__toggle--empty" : ""
              } ${!isCollapsed && hasChildren ? "template-editor__toggle--open" : ""}`}
              onClick={() => hasChildren && onToggleCollapse(item.id)}
              disabled={!hasChildren}
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          ) : null}
          <span
            className={`template-editor__item-icon template-editor__item-icon--${item.type}`}
          >
            {item.type === "folder" ? (
              <i className="fa-regular fa-folder"></i>
            ) : (
              <i className="fa-regular fa-image"></i>
            )}
          </span>
          <input
            ref={inputRef}
            className="rename__input template-editor__input"
            value={item.name}
            onChange={(e) => onNameChange(item.id, e.target.value)}
          />
        </div>
        <div className="template-editor__item-actions">
          {isFolder && (
            <>
              <button
                type="button"
                className="template-editor__action-button"
                onClick={() => onAddChild(item.id, "folder")}
                title="Add folder"
                aria-label="Add folder"
              >
                <i className="fa-regular fa-folder"></i>
              </button>
              <button
                type="button"
                className="template-editor__action-button"
                onClick={() => onAddChild(item.id, "comp")}
                title="Add composition"
                aria-label="Add composition"
              >
                <i className="fa-regular fa-image"></i>
              </button>
            </>
          )}
          <button
            type="button"
            className="template-editor__delete-button"
            onClick={() => onDelete(item.id)}
            title="Delete item"
          >
            <i className="fa-regular fa-trash"></i>
          </button>
        </div>
      </div>
          {hasChildren && !isCollapsed && (
        <div className="template-editor__children">
          {item.include.map((child) => (
            <TemplateItem
              key={child.id}
              item={child}
              depth={depth + 1}
              focusedId={focusedId}
              onNameChange={onNameChange}
              onAddChild={onAddChild}
              onDelete={onDelete}
              collapsedItems={collapsedItems}
              onToggleCollapse={onToggleCollapse}
              draggingItemId={draggingItemId}
              dropTarget={dropTarget}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragLeave={onDragLeave}
              onItemDrop={onItemDrop}
              setDropTarget={setDropTarget}
              canDrop={canDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NewTemplate({ selectedTag, buttonTag = null, generateButtonTags }) {
  const [isVisible, setIsVisible] = useVisible("NewTemplateGroupVisibility");
  const [capsTemplate, setCapsTemplate] = useVisible("capsTemplate");
  const [focusedId, setFocusedId] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const [templateType, setTemplateType] = useVisible("templateType", "Text");
  const [resolutionValue, setResolutionValue] = useVisible("resolutionValue", "full_hd");
  const [fpsValue, setFpsValue] = useVisible("fpsValue", "30");

  const [textTemplateState, setTextTemplateState] = useVisible("textTemplate");
  const [transitionTemplateState, setTransitionTemplateState] =
    useVisible("transitionTemplate");
  const [slideshowTemplateState, setSlideshowTemplateState] =
    useVisible("slideshowTemplate");
  const [customTemplates, setCustomTemplates] = useVisible("customTemplates", []);
  const [hiddenTemplates, setHiddenTemplates] = useVisible("hiddenTemplates", []);
  const [collapsedItems, setCollapsedItems] = useState({});
  const [newTemplateName, setNewTemplateName] = useState("");

  const templateOptions = useMemo(() => {
    const builtInOptions = [
      { value: "Text", name: "Text" },
      { value: "Transition", name: "Transition" },
      { value: "Slideshow", name: "Slideshow" },
    ].filter(({ value }) => !hiddenTemplates.includes(value));

    const customOptions = customTemplates.map((template) => ({
      value: buildCustomTemplateValue(template.id),
      name: template.name?.trim() || "Untitled template",
    }));

    return [...builtInOptions, ...customOptions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
  }, [customTemplates, hiddenTemplates]);
  const selectedTemplateOption = templateOptions.find(
    (option) => option.value === templateType
  );
  const selectedResolutionPreset =
    RESOLUTION_PRESETS.find((preset) => preset.value === resolutionValue) ||
    RESOLUTION_PRESETS[0];
  const isDeleteDisabled = templateOptions.length <= 1;
  const getTemplateStateByType = (type) => {
    if (isCustomTemplateType(type)) {
      const customId = getCustomTemplateId(type);
      return (
        customTemplates.find((template) => template.id === customId)?.data || null
      );
    }

    switch (type) {
      case "Text":
        return textTemplateState;
      case "Transition":
        return transitionTemplateState;
      case "Slideshow":
        return slideshowTemplateState;
      default:
        return null;
    }
  };

  const handleClick = (script) => {
    getCachedScript();

    const nextTemplate = getTemplateStateByType(templateType) || textTemplate;

    const fallbackResolution = Array.isArray(nextTemplate?.resolution)
      ? nextTemplate.resolution
      : Array.isArray(textTemplate.resolution)
      ? textTemplate.resolution
      : RESOLUTION_PRESETS[0].dimensions;

    const resolutionDimensions =
      selectedResolutionPreset?.dimensions || fallbackResolution;

    const currentValues = {
      ...nextTemplate,
      capsName: capsTemplate,
      resolution: Array.isArray(resolutionDimensions)
        ? [...resolutionDimensions]
        : resolutionDimensions,
      fps: fpsValue === "60" ? 60 : 30,
    };

    window.jsx.evalScript(
      `run(${script}('app.project.rootItem', ${JSON.stringify(
        currentValues
      )}));`,
      function (result) {}
    );
  };

  const handleEditClick = () => {
    if (!currentTemplate) return;

    setEditingTemplate(cloneTemplate(currentTemplate));
    setIsEditing(true);
  };

  useEffect(() => {
    if (!isEditing) {
      setCollapsedItems({});
    }
  }, [isEditing]);

  const persistTemplateChanges = (updatedTemplate) => {
    if (!updatedTemplate) {
      return;
    }

    const templateToStore = cloneTemplate(updatedTemplate);
    setCurrentTemplate(templateToStore);

    if (isCustomTemplateType(templateType)) {
      const customId = getCustomTemplateId(templateType);
      if (!customId) {
        return;
      }
      setCustomTemplates((prev) =>
        prev.map((template) =>
          template.id === customId ? { ...template, data: templateToStore } : template
        )
      );
      return;
    }

    switch (templateType) {
      case "Text":
        setTextTemplateState(templateToStore);
        setItem("textTemplate", JSON.stringify(templateToStore));
        break;
      case "Transition":
        setTransitionTemplateState(templateToStore);
        setItem("transitionTemplate", JSON.stringify(templateToStore));
        break;
      case "Slideshow":
        setSlideshowTemplateState(templateToStore);
        setItem("slideshowTemplate", JSON.stringify(templateToStore));
        break;
      default:
        break;
    }
  };

  const handleNameChange = (itemId, newName) => {
    setFocusedId(itemId);
    setEditingTemplate((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        items: updateItemName(prev.items, itemId, newName),
      };
    });
  };

  const handleAddItem = (parentId, type) => {
    const newItem = createTemplateItem(type);
    setFocusedId(newItem.id);

    setEditingTemplate((prev) => {
      if (!prev) return prev;

      if (!parentId) {
        return {
          ...prev,
          items: [...(prev.items || []), newItem],
        };
      }

      return {
        ...prev,
        items: addChildToItems(prev.items || [], parentId, newItem),
      };
    });

    if (parentId) {
      setCollapsedItems((prev) => {
        if (!prev[parentId]) {
          return prev;
        }

        const { [parentId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleDeleteItem = (itemId) => {
    setEditingTemplate((prev) => {
      if (!prev) return prev;

      const removal = removeItemById(prev.items || [], itemId);
      if (!removal.removed) {
        return prev;
      }

      return {
        ...prev,
        items: removal.items,
      };
    });
  };

  const handleDragStartItem = (itemId) => {
    setDraggingItemId(itemId);
  };

  const handleDragEndItem = () => {
    setDraggingItemId(null);
    setDropTarget(null);
  };

  const handleDragLeaveItem = (itemId) => {
    setDropTarget((prev) => {
      if (prev?.id === itemId) {
        return null;
      }
      return prev;
    });
  };

  const canDropOnItem = (targetId, position) => {
    if (!editingTemplate?.items || !draggingItemId) {
      return false;
    }
    if (targetId === draggingItemId) {
      return false;
    }
    if (isDescendant(editingTemplate.items, draggingItemId, targetId)) {
      return false;
    }
    if (position === "inside") {
      const targetItem = findItemById(editingTemplate.items, targetId);
      if (!targetItem || targetItem.type !== "folder") {
        return false;
      }
    }
    return true;
  };

  const handleItemDrop = (targetId, position) => {
    if (!draggingItemId || !canDropOnItem(targetId, position)) {
      return;
    }

    setEditingTemplate((prev) => {
      if (!prev) return prev;

      const moveResult = moveItemInTree(prev.items || [], draggingItemId, targetId, position);
      if (!moveResult.moved) {
        return prev;
      }

      return {
        ...prev,
        items: moveResult.items,
      };
    });

    if (position === "inside") {
      setCollapsedItems((prev) => {
        const { [targetId]: _, ...rest } = prev;
        return rest;
      });
    }

    setDraggingItemId(null);
    setDropTarget(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingTemplate(null);
    setFocusedId(null);
    setDraggingItemId(null);
    setDropTarget(null);
    setNewTemplateName("");
  };

  const handleSaveTemplate = () => {
    if (!editingTemplate) {
      handleCancelEdit();
      return;
    }

    persistTemplateChanges(editingTemplate);
    handleCancelEdit();
  };

  const handleSaveTemplateAsNew = () => {
    if (!editingTemplate) {
      return;
    }

    const trimmedName = newTemplateName.trim();
    if (!trimmedName) {
      return;
    }

    const templatePayload = cloneTemplate(editingTemplate);
    const newId = uuidv4();
    const newTemplate = {
      id: newId,
      name: trimmedName,
      data: templatePayload,
    };

    setCustomTemplates((prev) => [...prev, newTemplate]);
    const nextTypeValue = buildCustomTemplateValue(newId);
    setTemplateType(nextTypeValue);
    setCurrentTemplate(templatePayload);
    setNewTemplateName("");
    handleCancelEdit();
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplateOption || templateOptions.length <= 1) {
      return;
    }

    const shouldDelete = window.confirm(
      `Delete the template \"${selectedTemplateOption.name}\"? This action cannot be undone.`
    );
    if (!shouldDelete) {
      return;
    }

    if (isCustomTemplateType(templateType)) {
      const customId = getCustomTemplateId(templateType);
      if (!customId) {
        return;
      }
      setCustomTemplates((prev) =>
        prev.filter((template) => template.id !== customId)
      );
      return;
    }

    setHiddenTemplates((prev) =>
      prev.includes(templateType) ? prev : [...prev, templateType]
    );
  };

  const handleToggleCollapse = (itemId) => {
    setCollapsedItems((prev) => {
      if (prev[itemId]) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }

      return { ...prev, [itemId]: true };
    });
  };

  const collectFolderIds = (items, target = {}) => {
    if (!Array.isArray(items)) return target;
    items.forEach((item) => {
      if (Array.isArray(item.include) && item.include.length > 0) {
        target[item.id] = true;
        collectFolderIds(item.include, target);
      }
    });
    return target;
  };

  const handleCollapseAll = () => {
    if (!editingTemplate?.items) {
      setCollapsedItems({});
      return;
    }
    setCollapsedItems(collectFolderIds(editingTemplate.items, {}));
  };

  const handleExpandAll = () => {
    setCollapsedItems({});
  };

  useEffect(() => {
    if (!textTemplateState) {
      setTextTemplateState(textTemplate);
    }
    if (!transitionTemplateState) {
      setTransitionTemplateState(transitionTemplate);
    }
    if (!slideshowTemplateState) {
      setSlideshowTemplateState(slideshowTemplate);
    }
  }, []);

  useEffect(() => {
    setCurrentTemplate(getTemplateStateByType(templateType) || null);
  }, [
    templateType,
    textTemplateState,
    transitionTemplateState,
    slideshowTemplateState,
    customTemplates,
  ]);

  useEffect(() => {
    if (templateOptions.length === 0) {
      return;
    }
    if (!templateOptions.some((option) => option.value === templateType)) {
      setTemplateType(templateOptions[0].value);
    }
  }, [templateOptions, templateType, setTemplateType]);

  useEffect(() => {
    if (!RESOLUTION_PRESETS.some((preset) => preset.value === resolutionValue)) {
      setResolutionValue(RESOLUTION_PRESETS[0].value);
    }
  }, [resolutionValue, setResolutionValue]);

  useEffect(() => {
    if (fpsValue !== "30" && fpsValue !== "60") {
      setFpsValue("30");
    }
  }, [fpsValue, setFpsValue]);

  if (selectedTag && selectedTag !== buttonTag) {
    return null;
  }

  return (
    <>
      <Modal isOpen={isEditing} onClose={handleCancelEdit}>
        {editingTemplate && (
          <div className="template-editor-modal">
            <div className="template-editor-modal__header">
              <h3>Template structure</h3>
              <p>
                Add or remove folders and comps, then save to update the
                template.
              </p>
              <div className="template-editor-modal__toolbar">
                <button
                  type="button"
                  className="template-editor__toolbar-button"
                  onClick={handleExpandAll}
                >
                  Expand all
                </button>
                <button
                  type="button"
                  className="template-editor__toolbar-button"
                  onClick={handleCollapseAll}
                >
                  Collapse all
                </button>
              </div>
            </div>
            <div className="template-editor__items">
              {editingTemplate.items && editingTemplate.items.length > 0 ? (
                editingTemplate.items.map((item) => (
                  <TemplateItem
                    key={item.id}
                    item={item}
                    depth={0}
                    focusedId={focusedId}
                    onNameChange={handleNameChange}
                    onAddChild={handleAddItem}
                    onDelete={handleDeleteItem}
                    collapsedItems={collapsedItems}
                    onToggleCollapse={handleToggleCollapse}
                    draggingItemId={draggingItemId}
                    dropTarget={dropTarget}
                    onDragStart={handleDragStartItem}
                    onDragEnd={handleDragEndItem}
                    onDragLeave={handleDragLeaveItem}
                    onItemDrop={handleItemDrop}
                    setDropTarget={setDropTarget}
                    canDrop={canDropOnItem}
                  />
                ))
              ) : (
                <div className="template-editor__empty">
                  Structure is empty. Add a folder or composition to start.
                </div>
              )}
            </div>
            <div className="template-editor-modal__footer">
              <div className="template-editor-modal__actions">
                <button
                  type="button"
                  className="template-editor__action-button"
                  onClick={() => handleAddItem(null, "folder")}
                  title="Add folder"
                  aria-label="Add folder to root"
                >
                  <i className="fa-regular fa-folder"></i>
                </button>
                <button
                  type="button"
                  className="template-editor__action-button"
                  onClick={() => handleAddItem(null, "comp")}
                  title="Add composition"
                  aria-label="Add composition to root"
                >
                  <i className="fa-regular fa-image"></i>
                </button>
              </div>
              <div className="template-editor-modal__save-new">
                <input
                  type="text"
                  className="template-editor__input template-editor__new-name"
                  value={newTemplateName}
                  onChange={(event) => setNewTemplateName(event.target.value)}
                  placeholder="Template name"
                  aria-label="Custom template name"
                />
                <button
                  type="button"
                  className="template-editor__save-new"
                  onClick={handleSaveTemplateAsNew}
                  disabled={!newTemplateName.trim()}
                >
                  Save as new template
                </button>
              </div>
              <div className="template-editor-modal__buttons">
                <button
                  type="button"
                  className="template-editor__save"
                  onClick={handleSaveTemplate}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="template-editor__cancel"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <li
        onClick={() => setIsVisible(!isVisible)}
        className={`button-card resolution-toggle ${isVisible ? "active" : ""}`}
      >
        <div className="button-card__icon">
          <i className="fa-solid fa-plus"></i>
        </div>
        <div className="button-card__title">New Template</div>
        {generateButtonTags()}
      </li>
      <div
        className={`render-group ${isVisible ? "" : "render-group--hidden"}`}
      >
        <div className="render-group__checkbox-group">
          <div className="render-group__dropdown">
            <span className="render-group__dropdown-text">
              Template
              <span className="render-group__dropdown-actions">
                <div
                  className="render-group__dropdown-icon"
                  onClick={handleEditClick}
                  title="Edit structure"
                  aria-label="Edit template structure"
                >
                  <i className="fa-sharp fa-solid fa-pen-to-square"></i>
                </div>
                <div
                  className={`render-group__dropdown-icon render-group__dropdown-icon--danger ${
                    isDeleteDisabled ? "render-group__dropdown-icon--disabled" : ""
                  }`}
                  onClick={isDeleteDisabled ? undefined : handleDeleteTemplate}
                  title={
                    isDeleteDisabled
                      ? "At least one template must remain"
                      : "Delete template"
                  }
                  aria-label="Delete template"
                  aria-disabled={isDeleteDisabled}
                >
                  <i className="fa-regular fa-trash-can"></i>
                </div>
              </span>
            </span>
            <select
              id="templateType"
              name="templateType"
              onChange={(event) => setTemplateType(event.target.value)}
              value={templateType || ""}
              disabled={templateOptions.length === 0}
            >
              {templateOptions.length === 0 ? (
                <option value="">No templates available</option>
              ) : (
                templateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="render-group__dropdown">
            <span className="render-group__dropdown-text">Resolution</span>
            <select
              id="resolution"
              name="resolution"
              onChange={(event) => setResolutionValue(event.target.value)}
              value={selectedResolutionPreset?.value || ""}
            >
              {RESOLUTION_PRESETS.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          <div className="render-group__dropdown">
            <span className="render-group__dropdown-text">Framerate</span>
            <select
              id="fps"
              name="fps"
              onChange={(event) => setFpsValue(event.target.value)}
              value={fpsValue}
            >
              <option value="30">30</option>
              <option value="60">60</option>
            </select>
          </div>
        </div>

        <div className="render-group__checkbox-group">
          <div className="render-group__checkbox">
            <input
              type="checkbox"
              id="capsTemplate"
              checked={capsTemplate}
              onChange={() => setCapsTemplate(!capsTemplate)}
              className="hidden-checkbox"
            />

            <label htmlFor="capsTemplate">
              <span className="custom-checkbox"></span>
              <span className="label-text">Caps Name</span>
            </label>
          </div>
        </div>

        <div className="render-group__tools-grid">
          <div
            onClick={() =>
              handleClick("newTemplate", JSON.stringify(textTemplate))
            }
            className="render-group__button"
          >
            <i className="fa-regular fa-folder"></i>
            <span className="render-group__button-text">Create</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewTemplate;
